import { motion, Variants } from 'framer-motion';
import styled from 'styled-components';
import React, { useCallback, useContext } from 'react';
import { SideBarStateContext } from './SideBarStateProvider';
import SideBarStateButton from './SideBarStateButton';
import { SIDEBAR_ANIMATION_TIME } from './constants';
import _ from 'lodash';

const OPEN_WIDTH = 260;

const CollapsedSideBarOverlayDiv = styled(motion.div)<{ $isHovered: boolean }>`
  width: ${OPEN_WIDTH}px;
  position: absolute;
  height: calc(100vh - 58px);
  display: flex;
  background-color: var(--color-bg-secondary);
  flex-direction: column;
  overflow: visible;
  z-index: 12;
  border-right: 1px solid var(--color-bg-sunken);

  &::before {
    content: '';
    position: absolute;
    // Only add threshold to div when hovered
    inset: ${(props) => (props.$isHovered ? '0 -40px 0 230px;' : '0')};
    pointer-events: bounding-box;
  }
`;

const variants: Variants = {
  opened: {
    left: 0,
    transition: {
      duration: SIDEBAR_ANIMATION_TIME,
    },
  },
  closed: {
    left: -OPEN_WIDTH,
    transition: {
      duration: SIDEBAR_ANIMATION_TIME,
    },
  },
};

const CollapsedSideBarOverlay = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { aniState, setAniState } = useContext(SideBarStateContext);
  const animation = aniState.isHovered ? 'opened' : 'closed';
  const onMouseLeave = useCallback(() => {
    setAniState((current) => {
      if (current.isOpen) {
        return current;
      }

      return {
        ...current,
        isHovered: false,
      };
    });
  }, [setAniState]);

  if (aniState.isOpen && !aniState.isPinning) return null;

  return (
    <CollapsedSideBarOverlayDiv
      $isHovered={aniState.isHovered}
      onMouseLeave={_.debounce(onMouseLeave, 150)}
      style={{ pointerEvents: aniState.isOpen ? 'none' : 'auto' }}
      animate={animation}
      initial={animation}
      variants={variants}
    >
      {(!aniState.isOpen || aniState.isPinning) && (
        <>
          {aniState.isHovered && <SideBarStateButton />}
          {children}
        </>
      )}
    </CollapsedSideBarOverlayDiv>
  );
};

export default CollapsedSideBarOverlay;
